import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Space, Input, Switch, Typography, message, Select } from 'antd';
import { PlusOutlined, SaveOutlined, EditOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as Icons from '@ant-design/icons';
import { renderAntdIcon } from '../../utils/antdIcon';
import {
  listContactActivityTypes,
  createContactActivityType,
  updateContactActivityType,
  type ContactActivityTypeDTO,
} from '../../services/contactActivityTypes';

const { Text } = Typography;

type Row = ContactActivityTypeDTO & { _tmpId?: number; _isNew?: boolean };

const ContactActivityTypeManager: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState<number | string | null>(null);

  const iconOptions = useMemo(() => {
    const names = Object.keys(Icons)
      .filter((k) => k.endsWith('Outlined'))
      .sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }));
    return names.map((name) => ({
      label: (
        <Space size={8}>
          <span style={{ fontSize: 18 }}>{renderAntdIcon(name)}</span>
          <span>{name}</span>
        </Space>
      ),
      value: name,
    }));
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const list = await listContactActivityTypes();
      setRows((list || []).map((r) => ({ ...r })));
    } catch (e: any) {
      message.error(e?.message || 'Errore caricamento tipi attività contatto');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); }, []);

  const isEditing = (r: Row) => (r.id ?? r._tmpId) === editingKey;
  const updateRow = (key: number | string | undefined, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => ((r.id ?? r._tmpId) === key ? { ...r, ...patch } : r)));

  const addRow = () => {
    const tmp: Row = {
      _tmpId: Date.now() * -1,
      _isNew: true,
      id: undefined as any,
      label: '',
      icon: '',
      active: true,
    };
    setRows((prev) => [tmp, ...prev]);
    setEditingKey(tmp._tmpId!);
  };

  const startEdit = (r: Row) => setEditingKey(r.id ?? r._tmpId ?? '');
  const cancelEdit = () => setEditingKey(null);

  const save = async (r: Row) => {
    const key = r.id ?? r._tmpId;
    const label = (r.label || '').trim();
    if (!label) return message.warning('Inserisci l’etichetta');

    const payload: ContactActivityTypeDTO = {
      id: (r.id as number) ?? 0,
      label,
      icon: r.icon || null,
      active: !!r.active,
    };

    try {
      setLoading(true);
      if (r._isNew || !r.id) {
        const created = await createContactActivityType(payload);
        setRows((prev) => prev.map((x) => ((x.id ?? x._tmpId) === key ? { ...created } : x)));
        message.success('Tipologia creata');
      } else {
        const updated = await updateContactActivityType(r.id as number, payload);
        setRows((prev) => prev.map((x) => ((x.id ?? x._tmpId) === key ? { ...updated } : x)));
        message.success('Tipologia aggiornata');
      }
      setEditingKey(null);
    } catch (e: any) {
      message.error(e?.message || 'Errore salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<Row> = [
    {
      title: 'Icona',
      dataIndex: 'icon',
      width: 260,
      render: (_: any, r) =>
        isEditing(r) ? (
          <Select
            showSearch
            allowClear
            placeholder="Seleziona icona"
            style={{ width: '100%' }}
            value={r.icon || undefined}
            onChange={(val) => updateRow(r.id ?? r._tmpId, { icon: val || '' })}
            filterOption={(input, option) =>
              (option?.value as string)?.toLowerCase().includes(input.toLowerCase())
            }
            options={iconOptions}
            optionLabelProp="value"
            dropdownMatchSelectWidth={480}
          />
        ) : (
          <Space>
            <span style={{ fontSize: 18 }}>{renderAntdIcon(r.icon)}</span>
            <Text type={r.icon ? undefined : 'secondary'}>{r.icon || '—'}</Text>
          </Space>
        ),
    },
    {
      title: 'Etichetta',
      dataIndex: 'label',
      render: (v: string, r) =>
        isEditing(r) ? (
          <Input
            size="small"
            value={v}
            onChange={(e) => updateRow(r.id ?? r._tmpId, { label: e.target.value })}
            placeholder="Chiamata / Consulenza / Preventivo"
          />
        ) : (
          <Text>{v}</Text>
        ),
    },
    {
      title: 'Attivo',
      dataIndex: 'active',
      width: 110,
      align: 'center',
      render: (v: boolean, r) =>
        isEditing(r) ? (
          <Switch
            checked={!!v}
            onChange={(checked) => updateRow(r.id ?? r._tmpId, { active: checked })}
          />
        ) : (
          <Switch checked={!!v} disabled />
        ),
    },
    {
      key: 'actions',
      width: 140,
      align: 'right',
      render: (_: any, r) =>
        isEditing(r) ? (
          <Space>
            <Button type="primary" size="small" icon={<SaveOutlined />} onClick={() => save(r)} />
            <Button size="small" onClick={cancelEdit}>Annulla</Button>
          </Space>
        ) : (
          <Button size="small" icon={<EditOutlined />} onClick={() => startEdit(r)} />
        ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<PlusOutlined />} onClick={addRow}>Aggiungi Tipologia</Button>
        <Button icon={<ReloadOutlined />} onClick={fetchData}>Ricarica</Button>
      </Space>

      <Table<Row>
        size="small"
        rowKey={(r) => (r.id ?? r._tmpId)!}
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 820 }}
      />
    </div>
  );
};

export default ContactActivityTypeManager;
